import React, { useMemo, useState } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import StaffService from '../services/StaffService';
import { buildPasswordHash } from '../services/PasswordService';
import { verifyPassword } from '../utils/crypto';
import { useUser } from '../store/UserContext';

export default function UserProfilePage() {
  const { user, updateSession } = useUser();
  const staffService = useMemo(() => new StaffService(), []);
  const [status, setStatus] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [nextPassword, setNextPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  if (!user) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Sign in to manage your profile.</p>
      </Card>
    );
  }

  const staffRecord = staffService.getById(user.id);

  const handleEmailSave = () => {
    setStatus('');
    if (!email.trim()) {
      setStatus('Email is required.');
      return;
    }
    if (staffRecord) {
      staffService.update({
        ...staffRecord,
        email: email.trim(),
        loginEmail: email.trim()
      });
    }
    updateSession({ email: email.trim() });
    setStatus('Email updated.');
  };

  const handlePasswordSave = async () => {
    setStatus('');
    if (nextPassword.length < 8) {
      setStatus('Password must be at least 8 characters.');
      return;
    }
    if (nextPassword !== confirmPassword) {
      setStatus('Passwords do not match.');
      return;
    }
    if (staffRecord?.passwordHash) {
      const ok = await verifyPassword(currentPassword, staffRecord.passwordHash);
      if (!ok) {
        setStatus('Current password is incorrect.');
        return;
      }
    }
    const hash = await buildPasswordHash(nextPassword);
    if (staffRecord) {
      staffService.update({
        ...staffRecord,
        passwordHash: hash,
        passwordMustChange: false
      });
    }
    updateSession({ passwordMustChange: false });
    setCurrentPassword('');
    setNextPassword('');
    setConfirmPassword('');
    setStatus('Password updated.');
  };

  return (
    <div className="space-y-6">
      <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>My profile</h2>

      {user.passwordMustChange && (
        <div className="sp-alert sp-alert--warning text-sm">
          This account was seeded for setup. Please change your password now.
        </div>
      )}

      <Card className="p-6 space-y-4">
        <div>
          <h3 className="sp-value" style={{ fontWeight: 600 }}>Contact</h3>
          <p className="sp-muted">Update your login email.</p>
        </div>
        <div>
          <label className="sp-label">Email</label>
          <input
            className="sp-input mt-2 w-full"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleEmailSave} type="button">
            Save email
          </Button>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <div>
          <h3 className="sp-value" style={{ fontWeight: 600 }}>Password</h3>
          <p className="sp-muted">Set a new password for your account.</p>
        </div>
        {staffRecord?.passwordHash && (
          <div>
            <label className="sp-label">Current password</label>
            <input
              className="sp-input mt-2 w-full"
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </div>
        )}
        <div>
          <label className="sp-label">New password</label>
          <input
            className="sp-input mt-2 w-full"
            type="password"
            value={nextPassword}
            onChange={(event) => setNextPassword(event.target.value)}
          />
        </div>
        <div>
          <label className="sp-label">Confirm new password</label>
          <input
            className="sp-input mt-2 w-full"
            type="password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
        </div>
        {status && <div className="text-xs sp-warning-text">{status}</div>}
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handlePasswordSave} type="button">
            Update password
          </Button>
        </div>
      </Card>
    </div>
  );
}
